"use client";

import { Suspense, type ReactNode } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import ProductGrid from "../components/ProductGrid";
import Pager from "./Pager";
import { paginate } from "./listing";
import { inCategory } from "../../lib/categories";
import type { CatalogLite } from "../../lib/types";

export interface ShopListingProps {
  /** Every visible product, in display order. */
  products: CatalogLite[];
  /** Category chips (non-gender categories) and whether each holds products. */
  categories: { slug: string; label: string; populated: boolean }[];
  /** Brand links row, rendered on the server. */
  brandsNav: ReactNode;
}

const chipClass = (on: boolean) =>
  `border px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] transition ${
    on ? "border-ink bg-ink text-paper" : "border-ink/25 hover:border-ink"
  }`;

function Body({ products, categories, brandsNav, page, category }: ShopListingProps & { page?: string | null; category?: string | null }) {
  const cat = category || undefined;
  const filtered = !cat || cat === "all" ? products : products.filter((p) => inCategory(p.name, cat));
  const { items, current, totalPages } = paginate(filtered, page);
  const heading = !cat || cat === "all" ? "Shop" : (categories.find((c) => c.slug === cat)?.label ?? "Shop");
  // Only offer chips for categories that hold products (plus the one being
  // viewed), so a hidden-product cleanup never leaves dead-end chips behind.
  const chips = categories.filter((c) => c.populated || c.slug === cat);

  return (
    <>
      <div className="mb-8 flex items-end justify-between">
        <h1 className="display text-[clamp(2.5rem,6vw,4rem)] uppercase">{heading}</h1>
        <p className="eyebrow text-ink/60">{filtered.length.toLocaleString()} pieces</p>
      </div>

      {/* Category chips */}
      <nav aria-label="Shop categories" className="mb-10 flex flex-wrap gap-2">
        <Link href="/shop" className={chipClass(!cat)}>
          All
        </Link>
        {chips.map((c) => (
          <Link key={c.slug} href={`/shop?category=${c.slug}`} className={chipClass(cat === c.slug)}>
            {c.label}
          </Link>
        ))}
      </nav>

      {brandsNav}

      {items.length === 0 ? (
        <p className="py-20 text-center text-ink/60">No products in this category right now.</p>
      ) : (
        <ProductGrid products={items} />
      )}

      <Pager
        current={current}
        totalPages={totalPages}
        labelled={false}
        hrefFor={(n) => `/shop?${cat ? `category=${cat}&` : ""}page=${n}`}
      />
    </>
  );
}

function Live(props: ShopListingProps) {
  const sp = useSearchParams();
  return <Body {...props} page={sp.get("page")} category={sp.get("category")} />;
}

/**
 * /shop's listing: the route renders every product and reads no request data,
 * so it is cached (ISR). ?category= and ?page= are applied here from the URL;
 * the Suspense fallback is the unfiltered first page (the static HTML).
 */
export default function ShopListing(props: ShopListingProps) {
  return (
    <Suspense fallback={<Body {...props} />}>
      <Live {...props} />
    </Suspense>
  );
}
